import {Unit} from '@benchling/units';

import {MeasurementUnit} from 'constants/__generated/MeasurementUnit';
import {
  densityUnitTypePredicate,
  massUnitTypePredicate,
  molarityUnitTypePredicate,
  volumeUnitTypePredicate,
} from 'inventory/InventoryUnitUtil';
import {maybeGetUnitFromCacheBySymbol} from 'inventory/InventoryUnitUtil';
import {fetchUnitBySymbol} from 'inventory/InventoryUnitUtil';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/TableConstants';

export const DEFAULT_MOL_UNIT_SYMBOL = 'mol';
export const DEFAULT_MASS_UNIT_SYMBOL = 'g';
export const DEFAULT_VOLUME_UNIT_SYMBOL = 'L';
export const DEFAULT_DENSITY_UNIT_SYMBOL = 'g/L';
export const DEFAULT_MOLARITY_UNIT_SYMBOL = 'M';

// For stoichiometry amount calculations, we can use either mass or volume units.
export function stoichiometryAmountUnitTypePredicate(unitType: Unit['unitType']): boolean {
  return massUnitTypePredicate(unitType) || volumeUnitTypePredicate(unitType);
}

// For stoichiometry concentration calculations, we can use either density or molarity units.
export function stoichiometryConcentrationUnitTypePredicate(unitType: Unit['unitType']): boolean {
  return densityUnitTypePredicate(unitType) || molarityUnitTypePredicate(unitType);
}

/**
 * Get the factor (from GraphQL cache) to convert a unit reference to another unit.
 *
 * IMPORTANT: GraphQL stores conversion factors relative to SI base units (kg is BaseUnit for mass),
 * but chemistry calculations use g/mol for molecular weight. We need to convert from
 * the unit's SI base (kg) to chemistry standard (g).
 *
 * @param sourceUnitTypedValue - The unit typed value to convert
 * @param targetUnitSymbol - The symbol of the measurement unit to convert to
 * @returns The conversion factor as a number, null if unit cannot be determined, or if the unit
 * is not yet in the cache (triggers a background fetch for subsequent calls).
 */
export function getStandardChemistryUnitConversionFactorFromSymbol(
  sourceUnitTypedValue: TypedValue | null,
  targetUnitSymbol: string
): number | null {
  const unit = getUnitFromTypedValue(sourceUnitTypedValue);
  if (!unit) {
    return null;
  }
  const conversionFactorToSIBase = parseFloat(unit.conversionFactor);
  switch (targetUnitSymbol) {
    case DEFAULT_MASS_UNIT_SYMBOL: // kg -> g
    case DEFAULT_DENSITY_UNIT_SYMBOL: // kg/L -> g/L
      return conversionFactorToSIBase * 1000;
    default:
      return conversionFactorToSIBase;
  }
}

export function getUnitFromTypedValue(typedValue: TypedValue | null): MeasurementUnit | null {
  if (!typedValue || typedValue.type !== BaseTypedValueType.UNIT_REFERENCE) {
    // we can't determine the conversion factor if we don't have a unit reference
    return null;
  }
  const cachedUnit = maybeGetUnitFromCacheBySymbol(typedValue.unitSummary.symbol);
  if (!cachedUnit) {
    fetchUnitBySymbol(typedValue.unitSummary.symbol);
    return null;
  }
  return cachedUnit;
}
